import React, { useState } from 'react';
import { Box } from '@mui/material';
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
  GridFilterModel,
  GridRowIdGetter,
  GridRowParams,
  GridSelectionModel,
  GridValidRowModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { ButtonState, UniToolbar } from '../unitoolbar/unitoolbar';
import { IAction, ISelectedEntity } from '../unitoolbar/actions/interfaces';
import { DataGrid } from '../../areas/search/components/data-grid/data-grid';
import { keepScroll } from '../../areas/search/utilities/keepScroll';
import { redirectToRootEntity } from '../../areas/search/utilities/redirect-to-entity';
import { rowActionUrls } from '../../areas/search/contextProviders/row-action-urls';
import { DetailPanelToggle } from './detail-panel-toggle';
import { IStartPageTableFilter, StartPageTableFilterChooser } from './start-page-table-filter-chooser';
import { ToolbarTitle } from '../../areas/search/dashboard/components/dashboard-worklist-component/dashboard-toolbar-title';
import { GoToRootEntityToggle } from '../../areas/search/dashboard/components/dashboard-worklist-component/go-to-root-entity-toggle';

export interface IStartPageTableProps<R extends GridValidRowModel> {
  title: string;
  context: string;
  columns: GridColDef[];
  rows: R[];
  getRowId: GridRowIdGetter<R>;
  universe?: 'citizen' | 'company';
  getRootEntityId?: (row: R) => string | undefined;
  isLoading?: boolean;
  onRowClick?: (row: R) => void;
  onRefresh?: () => void;
  customTools?: React.ReactNode;
  getDetailPanelContent?: (params: GridRowParams<R>) => React.ReactNode;
  filters?: IStartPageTableFilter[];
  selectedFilterId?: string;
  onFilterChange?: (filterId: string) => void;
}

const worklistOnlyActions = ['EditInWorklistDesigner', 'AjustDiagramSize'];

// Listed explicitly so the grid puts the expand toggle after the selection checkbox
const detailPanelToggleColumn: GridColDef = {
  ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  width: 30,
  minWidth: 0,
  align: 'center',
  renderCell: ({ id, value }) => <DetailPanelToggle id={id} value={value} />,
};

export function StartPageTable<R extends GridValidRowModel>(props: IStartPageTableProps<R>) {
  const {
    title,
    context,
    columns,
    rows,
    getRowId,
    universe,
    getRootEntityId,
    isLoading,
    onRowClick,
    onRefresh,
    customTools,
    getDetailPanelContent,
    filters,
    selectedFilterId,
    onFilterChange,
  } = props;
  const apiRef = useGridApiRef();
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [goToRootEntity, setGoToRootEntity] = useState(false);
  const [filterChooserAnchorEl, setFilterChooserAnchorEl] = useState<HTMLButtonElement | null>(null);

  const selectedFilter = filters?.find(filter => filter.id === selectedFilterId);
  const canChooseFilter = !!filters && filters.length > 1;

  const selectedEntities: ISelectedEntity[] = selectionModel.map(id => {
    const row = apiRef.current.getRow(id) as R;
    return { entityId: id.toString(), rootEntityId: getRootEntityId?.(row), row };
  });
  const hiddenActions = onRefresh ? worklistOnlyActions : [...worklistOnlyActions, 'RefreshList'];
  const canGoToRootEntity = !!universe && !!getRootEntityId;

  function handleRowClick(params: GridRowParams) {
    const row = params.row as R;
    if (canGoToRootEntity && goToRootEntity) {
      redirectToRootEntity({ ...params, row: { ...row, rootEntityId: getRootEntityId(row) } }, rowActionUrls, context, universe);
      return;
    }
    onRowClick?.(row);
  }

  function handleFilterModelChange(model: GridFilterModel) {
    setIsFiltered(model.items.some(item => item.value !== undefined && item.value !== ''));
  }

  function handleToolbarButtonClick(action: IAction, _event: React.MouseEvent<HTMLButtonElement, MouseEvent>, updateButtonState: (item: IAction, state: ButtonState) => void) {
    if (action.id === 'RefreshList') {
      setSelectionModel([]);
      onRefresh?.();
    } else if (action.id === 'FilterList') {
      keepScroll(() => apiRef.current.showFilterPanel());
    } else if (action.id === 'Download') {
      updateButtonState(action, ButtonState.disabled);
      apiRef.current.exportDataAsCsv({ fileName: title, delimiter: ';' });
      updateButtonState(action, ButtonState.none);
    }
  }

  function handleFilterChange(filterId: string) {
    onFilterChange?.(filterId);
    setSelectionModel([]);
    setFilterChooserAnchorEl(null);
  }

  function renderToolbarTitle() {
    if (selectedFilter && canChooseFilter) {
      return (
        <ToolbarTitle
          title={selectedFilter.label}
          resultTypeName={title}
          numberOfResults={rows.length}
          selectedItemsCount={selectionModel.length}
          onClickNameChooser={event => setFilterChooserAnchorEl(event.currentTarget)}
        />
      );
    }
    return selectionModel.length > 0 ? (
      <h3 className="toolbar-title toolbar-title--withSelectedCheckboxes">{selectionModel.length} valgt</h3>
    ) : (
      <h3 className="toolbar-title">{selectedFilter ? `${selectedFilter.label} (${rows.length} ${title})` : `${title} (${rows.length})`}</h3>
    );
  }

  return (
    <>
      <UniToolbar
        context={context}
        universe={universe}
        selectedEntities={selectedEntities}
        rowCount={rows.length}
        title={renderToolbarTitle()}
        isFiltered={isFiltered}
        isLoading={isLoading}
        hideActionsById={hiddenActions}
        onButtonClick={handleToolbarButtonClick}
        onActionCompleted={onRefresh}
        customTools={
          <>
            {customTools}
            {canGoToRootEntity && <GoToRootEntityToggle universe={universe} checked={goToRootEntity} onChange={setGoToRootEntity} />}
          </>
        }
      />
      <Box sx={{ height: 'calc(100vh - 141px)', backgroundColor: '#E1E5E8' }}>
        <DataGrid
          mode="client"
          apiRef={apiRef}
          rows={rows}
          columns={getDetailPanelContent ? [detailPanelToggleColumn, ...columns] : columns}
          getRowId={getRowId}
          isLoading={isLoading}
          hasCheckboxForSelection
          selectionModel={selectionModel}
          onSelectedRow={setSelectionModel}
          onRowClick={handleRowClick}
          onFilterModelChange={handleFilterModelChange}
          disableAutoHeight
          disableColumnMenu
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelContent ? () => 'auto' : undefined}
        />
      </Box>
      {canChooseFilter && <StartPageTableFilterChooser filters={filters} anchorEl={filterChooserAnchorEl} onChange={handleFilterChange} onClose={() => setFilterChooserAnchorEl(null)} />}
    </>
  );
}
